import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DialogFrame } from "component/shared/dialog-frame";
import { FormField } from "component/shared/form-fields";
import { OutlinedButton } from "component/shared/outlined-button";
import { ClientBoardResource } from "api/resource/client-board";
import { mlsIdErrorMessage } from "component/shared/mls-id-dialogs/errors";
import { MlsIdBoard } from "model/agent-mls-ids";
import { MLS_ID_LABEL, PlainKind } from "../plain-table";
import { styles } from "./style";

// Shown in Add and Edit while the board waits on paperwork approval.
export const pendingNotice = (boardName: string) =>
	`${boardName} is pending approval. You can set up MLS IDs now, but listings won't display until the board is approved.`;

// A strict client-board write answers ALREADY_ADDED when the MLS ID is already on a row of the board, which on the
// plain table means it's already on Featured Listings there. Everything else words as the other MLS ID writes do.
const plainRefusal = (error: unknown, mlsId: string, boardName: string): string => {
	const { message, code } = (error ?? {}) as { message?: string; code?: string };
	if (code === "ALREADY_ADDED" || message === "ALREADY_ADDED") {
		return `${mlsId} is already on Featured Listings for ${boardName}.`;
	}
	return mlsIdErrorMessage(error);
};

export interface PlainValues {
	mlsId: string;
	// Single-agent accounts only.
	alternateId: string;
}

interface EditProps extends WithStyles<typeof styles> {
	open: boolean;
	kind: PlainKind;
	// The row to edit; null adds one.
	row: ClientBoardResource | null;
	boards: MlsIdBoard[];
	onSave: (row: ClientBoardResource | null, boardId: number, values: PlainValues) => Promise<unknown>;
	onClose: () => void;
}

// Add or edit a plain table row. The board is chosen on add and fixed on edit.
const Component: React.FC<EditProps> = ({ open, kind, row, boards, onSave, onClose, classes = {} }) => {
	const [boardId, setBoardId] = useState<number | "">("");
	const [mlsId, setMlsId] = useState("");
	const [alternateId, setAlternateId] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const label = MLS_ID_LABEL[kind];

	useEffect(() => {
		if (open) {
			setBoardId(row ? row.boardId : boards[0]?.boardId ?? "");
			setMlsId(row ? row.agentId : "");
			setAlternateId(row ? row.officeId : "");
			setError(null);
			setSaving(false);
		}
	}, [open, row, boards]);

	const save = async () => {
		if (!mlsId.trim()) {
			setError(`${label} is required.`);
			return;
		}
		if (boardId === "") {
			return;
		}
		setSaving(true);
		setError(null);
		try {
			await onSave(row, boardId, { mlsId: mlsId.trim(), alternateId: alternateId.trim() });
			onClose();
		} catch(e) {
			setError(plainRefusal(e, mlsId.trim(), boards.find((b) => b.boardId === boardId)?.name ?? ""));
			setSaving(false);
		}
	};

	const board = boards.find((b) => b.boardId === boardId);
	const boardName = board?.name ?? row?.boardName ?? "";
	return (
		<DialogFrame
			open={open}
			onClose={onClose}
			title={row ? "Edit MLS ID" : "Add MLS ID"}
			titleId="plain-mls-id-title"
			closeDisabled={saving}
			notice={error && <Mui.Alert severity="error">{error}</Mui.Alert>}
			actions={
				<>
					<OutlinedButton onClick={onClose} disabled={saving}>Cancel</OutlinedButton>
					<Mui.Button variant="contained" color="primary" onClick={save} disabled={saving}>
						{row ? "Save" : "Add"}
					</Mui.Button>
				</>
			}
		>
			{board?.pending && <Mui.Alert severity="warning">{pendingNotice(boardName)}</Mui.Alert>}
			{row ? (
				<div className={classes.fixedBoard}>
					<span className={classes.fixedBoardLabel}>MLS Board</span>
					<Mui.Typography>{boardName}</Mui.Typography>
				</div>
			) : (
				<Mui.TextField
					select
					label="MLS Board"
					value={boardId}
					onChange={(event) => setBoardId(Number(event.target.value))}
					fullWidth
				>
					{boards.map((board) => (
						<Mui.MenuItem key={board.boardId} value={board.boardId}>{board.name}</Mui.MenuItem>
					))}
				</Mui.TextField>
			)}
			<FormField
				id="plain-mls-id"
				label={label}
				required
				value={mlsId}
				onChange={(event) => setMlsId(event.target.value)}
				onKeyDown={(event) => event.key === "Enter" && save()}
				autoFocus
			/>
			{kind === "singleAgent" && (
				<FormField
					id="plain-office-id"
					label="Office MLS ID"
					value={alternateId}
					onChange={(event) => setAlternateId(event.target.value)}
					onKeyDown={(event) => event.key === "Enter" && save()}
				/>
			)}
		</DialogFrame>
	);
};

export const PlainEditDialog = withStyles(Component, styles);
